/**
 * BUG-2445: "has this estimate already been billed?" without reading the whole
 * local orders table on every Pay.
 *
 * The check itself is unchanged (cartIdentityGuard.findDuplicateEstimateOrder:
 * any saved order with the same estimateId and a different sOrderID, across ALL
 * local orders). What changed is how the candidate rows are found. The old call
 * sites ran db.orders.toArray() three times per estimate bill (Early + Late in
 * processOrderApproved, once in syncOrders); on a long-lived till (16k+ orders /
 * 200 MB) each read takes seconds. estimateId is not an indexed field, and it is
 * not added as one here: a Dexie version bump is not needed for this.
 *
 *  - Web (Dexie / IndexedDB): one full read per page load fills an in-memory map
 *    of estimateId -> sOrderIDs. Dexie "creating" / "updating" hooks on the orders
 *    table keep it current for every later write made from this page. Each check
 *    also re-reads the pending rows and this till session's rows through their
 *    indexes, so rows written by another context on the same IndexedDB are seen.
 *    A map hit is never trusted on its own: the row is read back by primary key
 *    and must still carry the estimateId.
 *  - CW Browser / app bridge (SQLite): the bridge can filter on estimateId inside
 *    the database, so only matching rows cross IPC. That is used only after it
 *    has been proven on this device: the first checks still do the full read, and
 *    the filtered query must return a known estimate order before it is trusted.
 *  - Anything else, or any failure of the above: the old full read.
 *
 * This file has no imports on purpose (dataIntegrity.js uses it too).
 */

const lookupStates = new WeakMap();
const getState = (dbAdapter) => {
  let state = lookupStates.get(dbAdapter);
  if (!state) {
    state = { hooked: false, ready: null, byEstimate: new Map(), whereProven: false };
    lookupStates.set(dbAdapter, state);
  }
  return state;
};

const isDuplicate = (o, estimateId, excludeSOrderID) => !!o && o.estimateId === estimateId && o.sOrderID !== excludeSOrderID;
const firstDuplicate = (rows, estimateId, excludeSOrderID) => (rows || []).find((o) => isDuplicate(o, estimateId, excludeSOrderID)) || null;

const noteEstimateOrder = (state, estimateId, sOrderID) => {
  if (!estimateId || !sOrderID) return;
  let ids = state.byEstimate.get(estimateId);
  if (!ids) {
    ids = new Set();
    state.byEstimate.set(estimateId, ids);
  }
  ids.add(sOrderID);
};

const getDexieOrdersTable = (dbAdapter) => {
  const table = dbAdapter?.orders?._table;
  return table && typeof table.hook === "function" ? table : null;
};

/**
 * Start recording estimate orders written through this adapter (Dexie only).
 * Must be called BEFORE a full read whose rows are later handed to
 * primeEstimateOrderLookup, so no write between the two is missed.
 * @returns {boolean} true when writes are being recorded
 */
export function trackEstimateOrderWrites(dbAdapter) {
  const table = getDexieOrdersTable(dbAdapter);
  if (!table) return false;
  const state = getState(dbAdapter);
  if (!state.hooked) {
    // Hook bodies must return undefined: a returned value would be taken by Dexie
    // as the primary key ("creating") or as extra modifications ("updating").
    table.hook("creating", (primKey, obj) => {
      noteEstimateOrder(state, obj?.estimateId, obj?.sOrderID ?? primKey);
    });
    table.hook("updating", (mods, primKey, obj) => {
      const estimateId = mods && Object.prototype.hasOwnProperty.call(mods, "estimateId") ? mods.estimateId : obj?.estimateId;
      noteEstimateOrder(state, estimateId, obj?.sOrderID ?? primKey);
    });
    state.hooked = true;
  }
  return true;
}

/**
 * Fill the lookup from a full orders read the caller already paid for.
 * Only valid when trackEstimateOrderWrites returned true before that read began.
 */
export function primeEstimateOrderLookup(dbAdapter, allOrders) {
  const state = getState(dbAdapter);
  if (!state.hooked || !Array.isArray(allOrders)) return;
  allOrders.forEach((o) => noteEstimateOrder(state, o?.estimateId, o?.sOrderID));
  if (!state.ready) state.ready = Promise.resolve();
}

const findViaDexie = async (dbAdapter, state, estimateId, excludeSOrderID, tillSessionId) => {
  const orders = dbAdapter.orders;
  trackEstimateOrderWrites(dbAdapter);
  if (!state.ready) {
    state.ready = orders.toArray().then((rows) => {
      rows.forEach((o) => noteEstimateOrder(state, o?.estimateId, o?.sOrderID));
    });
    // A failed scan must not be remembered as done.
    state.ready.catch(() => {
      state.ready = null;
    });
  }
  await state.ready;

  // Rows another context on the same IndexedDB may have written since the scan.
  const recent = await orders.where("isSynced").anyOf([0, 2, "0", "2"]).toArray();
  const direct = firstDuplicate(recent, estimateId, excludeSOrderID);
  if (direct) return direct;
  if (tillSessionId) {
    const sessionRows = await orders.where("tillSessionId").equals(tillSessionId).toArray();
    const inSession = firstDuplicate(sessionRows, estimateId, excludeSOrderID);
    if (inSession) return inSession;
  }

  for (const sOrderID of Array.from(state.byEstimate.get(estimateId) || [])) {
    if (sOrderID === excludeSOrderID) continue;
    const row = await orders.get(sOrderID);
    if (isDuplicate(row, estimateId, excludeSOrderID)) return row;
  }
  return null;
};

const findViaFilteredQuery = async (dbAdapter, state, estimateId, excludeSOrderID) => {
  const orders = dbAdapter.orders;
  if (state.whereProven) {
    return firstDuplicate(await orders.where("estimateId").equals(estimateId).toArray(), estimateId, excludeSOrderID);
  }
  const allOrders = await orders.toArray();
  const known = allOrders.find((o) => o?.estimateId && o?.sOrderID);
  if (known && typeof orders.where === "function") {
    try {
      const rows = await orders.where("estimateId").equals(known.estimateId).toArray();
      state.whereProven = Array.isArray(rows) && rows.length < allOrders.length && rows.some((o) => o?.sOrderID === known.sOrderID && o?.estimateId === known.estimateId);
    } catch (probeErr) {
      console.warn("[BUG-2445] estimateId query not usable on this device, keeping the full read:", probeErr?.message);
    }
  }
  return firstDuplicate(allOrders, estimateId, excludeSOrderID);
};

/**
 * @param {Object} dbAdapter - database adapter (db)
 * @param {string} estimateId
 * @param {string} excludeSOrderID - the current cart's own sOrderID (a resave of
 *   the same order is not a duplicate)
 * @param {{ tillSessionId?: string }} [opts]
 * @returns {Promise<Object|null>} the saved order that already billed this estimate
 */
export async function findDuplicateEstimateOrderInDb(dbAdapter, estimateId, excludeSOrderID, { tillSessionId } = {}) {
  if (!estimateId) return null;
  const state = getState(dbAdapter);
  if (getDexieOrdersTable(dbAdapter)) {
    try {
      return await findViaDexie(dbAdapter, state, estimateId, excludeSOrderID, tillSessionId);
    } catch (lookupErr) {
      console.warn("[BUG-2445] estimate lookup failed, using the full read:", lookupErr?.message);
      return firstDuplicate(await dbAdapter.orders.toArray(), estimateId, excludeSOrderID);
    }
  }
  return findViaFilteredQuery(dbAdapter, state, estimateId, excludeSOrderID);
}
